import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Tooltip, type MenuProps } from 'antd';
import {
  ArrowLeft,
  LayoutGrid,
  Package as PackageIcon,
  FileText,
  ShieldCheck,
  ListChecks,
  ClipboardCheck,
  BarChart3,
  Users,
  Hammer,
  Menu as MenuIcon,
} from 'lucide-react';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
  useUserStore,
  selectHasRole,
  selectHasAnyRole,
  useBackLinkStore,
  useHeaderContentStore,
  useSidebarStore,
  useNavigationGuardStore,
  useSurfaceModeStore,
  useSessionWizardStore,
  type SurfaceMode,
} from '@/store';
import { useBreakpoint, useIsGuest, useLiquidGlass } from '@/hooks';
import { USER_ROLES, FORM_GLASS_SETTINGS } from '@/constants';
import { ROUTES } from '@/routes';
import { clearStoredGuestSessionToken } from '@/utils/guestSession';
import { resetAnalytics } from '@/services/analytics';
import { Logo } from '@/components';
import { UserMenu } from '@/components/UserMenu';
import { SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from '@/styles';
import styles from './MainLayout.module.scss';

const { Header, Sider, Content } = Layout;

// Same material as the auth forms (FORM_GLASS_SETTINGS), but fully rounded
// (pill) rather than a fixed 24px corner radius — useLiquidGlass clamps
// radius to the element's own half-height, so any value at or above that
// (this bar is 52px tall, half = 26px) resolves to a true pill regardless
// of exact height.
const HEADER_BAR_GLASS_SETTINGS = { ...FORM_GLASS_SETTINGS, radius: 999 };

interface MainLayoutProps {
  children?: React.ReactNode;
}

const SidebarToggleIcon: React.FC = () => (
  <svg width="1em" height="1em" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3 2C2.44772 2 2 2.44772 2 3V17C2 17.5523 2.44772 18 3 18H17C17.5523 18 18 17.5523 18 17V3C18 2.44772 17.5523 2 17 2H3ZM3 0H17C18.6569 0 20 1.34315 20 3V17C20 18.6569 18.6569 20 17 20H3C1.34315 20 0 18.6569 0 17V3C0 1.34315 1.34315 0 3 0Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 1C6 0.447715 6.44772 0 7 0C7.55228 0 8 0.447715 8 1V19C8 19.5523 7.55228 20 7 20C6.44772 20 6 19.5523 6 19V1Z"
      fill="currentColor"
    />
  </svg>
);

interface SidebarToggleButtonProps {
  title: string;
  ariaLabel: string;
  onClick: () => void;
}

const SidebarToggleButton: React.FC<SidebarToggleButtonProps> = ({ title, ariaLabel, onClick }) => (
  <Tooltip title={title} placement="right" classNames={{ root: 'ant-menu-inline-collapsed-tooltip' }}>
    <Button
      type="text"
      icon={<SidebarToggleIcon />}
      onClick={onClick}
      className={styles.trigger}
      aria-label={ariaLabel}
    />
  </Tooltip>
);

const MENU_ROUTE_MAP: Record<string, string> = {
  sessions: ROUTES.SESSIONS,
  buyPackages: ROUTES.PACKAGES,
  evaluate: ROUTES.EVALUATE,
  results: ROUTES.ADMIN_ASSESSMENTS,
  manageUsers: ROUTES.ADMIN_USERS,
  questionBank: ROUTES.ADMIN_QUESTIONS,
  htsQuestionnaires: ROUTES.ADMIN_HTS_QUESTIONNAIRES,
  questionSets: ROUTES.ADMIN_QUESTION_SETS,
  packages: ROUTES.ADMIN_PACKAGES,
  traces: ROUTES.ADMIN_TRACES,
};

const ROUTE_KEY_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(MENU_ROUTE_MAP).map(([key, route]) => [route, key])
);

// Candidates (role USER, not a guest) get no sidebar — just these two
// destinations, surfaced as a centered pill in the header (desktop) or an
// icon tab bar pinned to the bottom of the viewport (mobile).
const CANDIDATE_NAV_ITEMS = [
  { key: 'sessions', label: 'Sessions', route: ROUTES.SESSIONS, icon: LayoutGrid },
  { key: 'buyPackages', label: 'Packages', route: ROUTES.PACKAGES, icon: PackageIcon },
];

// Roles that can switch into the Admin surface. A COACH's admin sidebar is
// just Results; everything else there is SUPER_ADMIN-only.
const ADMIN_SURFACE_ROLES = [USER_ROLES.SUPER_ADMIN, USER_ROLES.COACH];

const SIDER_WIDTH_EXPANDED = parseInt(SIDEBAR_WIDTH_EXPANDED, 10);
const SIDER_WIDTH_COLLAPSED = parseInt(SIDEBAR_WIDTH_COLLAPSED, 10);

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { signOut } = useClerk();
  const { user: clerkUser } = useUser();
  const { user, clearUser } = useUserStore();
  const isSuperAdmin = useUserStore(selectHasRole(USER_ROLES.SUPER_ADMIN));
  const canUseAdminSurface = useUserStore(selectHasAnyRole(ADMIN_SURFACE_ROLES));
  const resetSessionWizard = useSessionWizardStore((s) => s.resetWizard);
  // Guests get no sidebar and no avatar/sign-out: none of that chrome implies
  // an identity they have.
  const isGuest = useIsGuest();
  // A SUPER_ADMIN or COACH can switch between the Admin surface (sidebar,
  // admin tools) and the same "Practice" surface a candidate sees — see the
  // breadcrumb dropdown beside the logo. Every other role always renders
  // the candidate surface.
  const surfaceMode = useSurfaceModeStore((s) => s.mode);
  const setSurfaceMode = useSurfaceModeStore((s) => s.setMode);
  const inAdminMode = canUseAdminSurface && surfaceMode === 'admin';
  // A signed-in candidate (role USER), or a SUPER_ADMIN currently in
  // Practice mode, has only Sessions and Packages to get to — the full
  // admin sidebar is wasted space for two destinations, so they get the
  // centered pill / bottom tab bar instead. Only Admin mode keeps the
  // sidebar.
  const isCandidate = !isGuest && !inAdminMode;
  const showSider = !isGuest && inAdminMode;
  const headerGlassRef = useLiquidGlass<HTMLDivElement>(HEADER_BAR_GLASS_SETTINGS);
  const endSessionGlassRef = useLiquidGlass<HTMLButtonElement>(HEADER_BAR_GLASS_SETTINGS);
  const [mobileSiderOpen, setMobileSiderOpen] = React.useState(false);
  const onBack = useBackLinkStore((s) => s.onBack);
  const backLinkLabel = useBackLinkStore((s) => s.label);
  const headerContent = useHeaderContentStore((s) => s.content);
  const headerActionContent = useHeaderContentStore((s) => s.actionContent);
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed, toggleCollapsed } = useSidebarStore();
  const guardNavigation = useNavigationGuardStore((s) => s.guardNavigation);
  const [collapsedHover, setCollapsedHover] = React.useState(false);
  const { isBelow } = useBreakpoint();
  const siderIsDrawer = isBelow('2xl');
  const effectiveCollapsed = collapsed && !siderIsDrawer;

  // Pages with custom header content (e.g. SessionInterview) render the
  // Header as position: fixed so content can scroll underneath it with a
  // blur fade — which pulls it out of flow, so nothing pushes scrollable
  // content down to clear it anymore. Measuring the header's real rendered
  // height here (it varies with the two-line title block, the mode toggle
  // row, and mobile flex-wrap) and exposing it as a CSS var lets those pages
  // reserve exactly enough top clearance instead of guessing a fixed value.
  const headerRef = React.useRef<HTMLDivElement>(null);
  const headerHeight = useHeaderContentStore((s) => s.height);
  const setHeaderMetrics = useHeaderContentStore((s) => s.setHeaderMetrics);

  React.useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setHeaderMetrics({
        height: entry.contentRect.height,
        bottomOffset: el.getBoundingClientRect().bottom,
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      setHeaderMetrics(null);
    };
  }, [setHeaderMetrics]);

  React.useEffect(() => {
    if (!collapsed) setCollapsedHover(false);
  }, [collapsed]);

  // Covers back/forward navigation too, not just an in-drawer menu click.
  React.useEffect(() => {
    setMobileSiderOpen(false);
  }, [location.pathname]);

  // surfaceMode is independent app state, not derived from the URL — so a
  // fresh load (or any navigation that didn't go through
  // handleSurfaceModeChange, e.g. a direct link or back/forward) can leave
  // it out of sync with which surface the current route actually belongs
  // to. Reconcile whenever the route unambiguously belongs to one surface.
  React.useEffect(() => {
    if (!canUseAdminSurface) return;
    const path = location.pathname;
    const isAdminRoute = path.startsWith(ROUTES.ADMIN) || path === ROUTES.EVALUATE;
    const isCandidateRoute = path.startsWith(ROUTES.SESSIONS) || path.startsWith(ROUTES.PACKAGES);
    if (isAdminRoute && surfaceMode !== 'admin') setSurfaceMode('admin');
    else if (isCandidateRoute && surfaceMode !== 'candidate') setSurfaceMode('candidate');
  }, [location.pathname, canUseAdminSurface, surfaceMode, setSurfaceMode]);

  const handleCollapsedHoverEnter = () => {
    if (collapsed) setCollapsedHover(true);
  };

  const handleCollapsedHoverLeave = () => {
    setCollapsedHover(false);
  };

  const handleBorderZoneClick = () => {
    if (collapsed) toggleCollapsed();
  };

  const handleSidebarToggleClick = () => {
    if (siderIsDrawer) {
      setMobileSiderOpen(false);
    } else {
      toggleCollapsed();
    }
  };

  const staticPrefix = (route: string) => route.slice(0, route.indexOf(':'));

  const selectedKey =
    ROUTE_KEY_MAP[location.pathname] ??
    (location.pathname.startsWith(ROUTES.ADMIN_QUESTION_SETS)
      ? 'questionSets'
      : location.pathname.startsWith(ROUTES.ADMIN_HTS_QUESTIONNAIRES)
        ? 'htsQuestionnaires'
        : location.pathname.startsWith(ROUTES.ADMIN_PACKAGES)
          ? 'packages'
          : location.pathname.startsWith(staticPrefix(ROUTES.ADMIN_SESSION_RESULTS)) ||
              location.pathname.startsWith(staticPrefix(ROUTES.ADMIN_HTS_SUBMISSION_RESULTS))
            ? 'results'
            : 'sessions');

  // The sidebar only ever renders in Admin mode now — Sessions/Assessment
  // Packages (the candidate's own store) live in the candidate pill/tab bar
  // instead, so they're not duplicated here.
  const menuItems: MenuProps['items'] = !isSuperAdmin
    ? [{ key: 'results', icon: <BarChart3 size={effectiveCollapsed ? 20 : 18} />, label: 'Results' }]
    : effectiveCollapsed
    ? [
        { key: 'questionBank', icon: <FileText size={20} />, label: 'Question Bank' },
        { key: 'htsQuestionnaires', icon: <ShieldCheck size={20} />, label: 'HTS Questionnaires' },
        { key: 'questionSets', icon: <ListChecks size={20} />, label: 'Question Sets' },
        { key: 'packages', icon: <PackageIcon size={20} />, label: 'Assessment Packages' },
        { type: 'divider' as const, key: 'builder-divider' },
        { key: 'evaluate', icon: <ClipboardCheck size={20} />, label: 'Evaluate' },
        { key: 'results', icon: <BarChart3 size={20} />, label: 'Results' },
        { key: 'manageUsers', icon: <Users size={20} />, label: 'Manage Users' },
      ]
    : [
        {
          key: 'assessmentBuilder',
          icon: <Hammer size={18} />,
          label: 'Assessment Builder',
          className: styles.builderGroup,
          children: [
            { key: 'questionBank', icon: <FileText size={18} />, label: 'Question Bank' },
            { key: 'htsQuestionnaires', icon: <ShieldCheck size={18} />, label: 'HTS Questionnaires' },
            { key: 'questionSets', icon: <ListChecks size={18} />, label: 'Question Sets' },
            { key: 'packages', icon: <PackageIcon size={18} />, label: 'Assessment Packages' },
          ],
        },
        { key: 'evaluate', icon: <ClipboardCheck size={18} />, label: 'Evaluate' },
        { key: 'results', icon: <BarChart3 size={18} />, label: 'Results' },
        { key: 'manageUsers', icon: <Users size={18} />, label: 'Manage Users' },
      ];

  const handleMenuClick = ({ key }: { key: string }) => {
    const route = MENU_ROUTE_MAP[key];
    if (!route) return;
    setMobileSiderOpen(false);
    if (guardNavigation) {
      guardNavigation(route);
      return;
    }
    navigate(route);
  };

  const handleSurfaceModeChange = (mode: SurfaceMode) => {
    setMobileSiderOpen(false);
    if (mode === surfaceMode) return;
    setSurfaceMode(mode);
    const adminHome = isSuperAdmin ? ROUTES.ADMIN_QUESTIONS : ROUTES.ADMIN_ASSESSMENTS;
    const target = mode === 'admin' ? adminHome : ROUTES.SESSIONS;
    if (guardNavigation) {
      guardNavigation(target);
      return;
    }
    navigate(target);
  };

  const handleToggleMobileSider = () => setMobileSiderOpen((v) => !v);

  const handleCandidateNavClick = (route: string) => {
    if (guardNavigation) {
      guardNavigation(route);
      return;
    }
    navigate(route);
  };

  const handleLogout = async () => {
    await signOut();
    // Also drop any guest session token: it outlives the Clerk session, and
    // ProtectedRoute treats a stored one as authoritative whenever Clerk reports
    // nobody signed in — so leaving it would land the user back in that guest
    // session instead of signed out.
    clearStoredGuestSessionToken();
    clearUser();
    resetAnalytics();
    resetSessionWizard();
    navigate(ROUTES.LOGIN);
  };

  // A guest never had a Clerk session to sign out of — clearing the stored
  // token and returning to the redeem page is the whole of "ending" it.
  const handleEndGuestSession = () => {
    navigate(ROUTES.REDEEM);
    clearStoredGuestSessionToken();
    clearUser();
    resetAnalytics();
    resetSessionWizard();
  };

  const displayName = user?.firstName || clerkUser?.firstName || "User";
  const avatarUrl = clerkUser?.imageUrl;

  return (
    <Layout className={`${styles.layout} ${!showSider ? 'no-sider-layout' : ''}`}>
      {showSider && mobileSiderOpen && (
        <div className={styles.scrim} onClick={() => setMobileSiderOpen(false)} aria-hidden="true" />
      )}

      {showSider && (
        <Sider
          className={mobileSiderOpen ? `${styles.sider} ${styles.siderMobileOpen}` : styles.sider}
          width={SIDER_WIDTH_EXPANDED}
          collapsed={effectiveCollapsed}
          collapsedWidth={siderIsDrawer ? SIDER_WIDTH_EXPANDED : SIDER_WIDTH_COLLAPSED}
          trigger={null}
        >
          <div
            className={effectiveCollapsed ? `${styles.logo} ${styles.logoCollapsed}` : styles.logo}
            onMouseEnter={handleCollapsedHoverEnter}
            onMouseLeave={handleCollapsedHoverLeave}
          >
            {effectiveCollapsed ? (
              <div className={collapsedHover ? `${styles.logoBadge} ${styles.logoBadgeToggle}` : styles.logoBadge}>
                {collapsedHover ? (
                  <SidebarToggleButton title="Show sidebar" ariaLabel="Expand sidebar" onClick={handleSidebarToggleClick} />
                ) : (
                  <Logo type="symbol" mode="dark" />
                )}
              </div>
            ) : (
              <>
                <Logo type="lettertype" mode="light" />
                <SidebarToggleButton title="Hide sidebar" ariaLabel="Collapse sidebar" onClick={handleSidebarToggleClick} />
              </>
            )}
          </div>

          {effectiveCollapsed && (
            <div
              className={styles.borderHoverZone}
              onMouseEnter={handleCollapsedHoverEnter}
              onMouseLeave={handleCollapsedHoverLeave}
              onClick={handleBorderZoneClick}
            />
          )}

          <div className={styles.menuClip}>
            <Menu
              key={effectiveCollapsed ? 'sidebar-menu-collapsed' : 'sidebar-menu-expanded'}
              mode="inline"
              inlineCollapsed={effectiveCollapsed}
              selectedKeys={[selectedKey]}
              openKeys={['assessmentBuilder']}
              onOpenChange={() => {}}
              items={menuItems}
              onClick={handleMenuClick}
              className={styles.menu}
            />
          </div>
        </Sider>
      )}

      <Layout
        className={`${styles.mainArea} ${!showSider ? styles.mainAreaNoSider : ''}`}
        style={headerHeight !== null ? ({ '--dynamic-header-height': `${headerHeight}px` } as React.CSSProperties) : undefined}
      >
        <Header
          ref={headerRef}
          className={headerContent ? `${styles.header} ${styles.headerWithContent}` : styles.header}
        >
          <div
            key={`${showSider}-${!!headerContent}`}
            className={styles.headerBar}
            ref={showSider || headerContent ? undefined : headerGlassRef}
          >
            <div className={styles.headerLeft}>
              {headerContent ??
                (isGuest || isCandidate ? (
                  <div className={styles.noSiderLogoBox}>
                    <Logo type="lettertype" mode="light" className={styles.noSiderLogo} />
                  </div>
                ) : onBack ? (
                  <button type="button" className={styles.backLink} onClick={onBack}>
                    <ArrowLeft size={16} />
                    {backLinkLabel ?? 'Back'}
                  </button>
                ) : (
                  // Desktop: the Sider already shows the logo in its own
                  // header strip. Mobile: the Sider is an off-canvas drawer, so
                  // this is the only place it's visible — plus the hamburger
                  // that opens it.
                  <div className={styles.adminMobileBar}>
                    <button
                      type="button"
                      className={styles.hamburgerBtn}
                      aria-label="Open sidebar menu"
                      onClick={handleToggleMobileSider}
                    >
                      <MenuIcon style={{ width: '1em', height: '1em' }} />
                    </button>
                    <Logo type="lettertype" mode="light" className={styles.noSiderLogo} />
                  </div>
                ))}
            </div>

            {isCandidate && !headerContent && (
              <nav className={styles.candidateNav} aria-label="Main">
                {CANDIDATE_NAV_ITEMS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={styles.candidateNavItem}
                    aria-current={selectedKey === item.key}
                    onClick={() => handleCandidateNavClick(item.route)}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            )}

            {/* Rendered only when there's actually something to show — an
                empty div here would still claim .headerBar's flex `gap`
                against .headerLeft, leaving a stray blank strip on pages
                with custom header content like SessionInterview's (which
                owns its entire header row, including exiting it, so neither
                the guest's own End Session action nor the avatar menu belong
                here in that case). */}
            {!headerContent && (
              <div className={styles.headerRight}>
                {isGuest ? (
                  <>
                    {headerActionContent}
                    <button
                      type="button"
                      ref={endSessionGlassRef}
                      className={styles.endGuestSessionBtn}
                      onClick={handleEndGuestSession}
                    >
                      <span>End Session</span>
                    </button>
                  </>
                ) : (
                  <UserMenu
                    displayName={displayName}
                    email={user?.email}
                    avatarUrl={avatarUrl}
                    showSurfaceSwitch={canUseAdminSurface}
                    isAdminSurface={inAdminMode}
                    onSwitchSurface={() => handleSurfaceModeChange(inAdminMode ? 'candidate' : 'admin')}
                    onSignOut={handleLogout}
                  />
                )}
              </div>
            )}
          </div>
        </Header>

        <Content
          className={
            isCandidate && !headerContent ? `${styles.content} ${styles.contentWithTabbar}` : styles.content
          }
        >
          {children || <Outlet />}
        </Content>

        {isCandidate && !headerContent && (
          <nav className={styles.tabbar} aria-label="Main">
            {CANDIDATE_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={styles.tabbarItem}
                  aria-current={selectedKey === item.key}
                  onClick={() => handleCandidateNavClick(item.route)}
                >
                  <Icon aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        )}
      </Layout>
    </Layout>
  );
};

export default MainLayout;
